import { Box, Stack, SxProps, Theme, Typography } from '@mui/material';
import CuredLogo from 'assets/images/svg/cured-logo-circle-green-no-text.svg';
import HtmlRenderer from 'domains/core/components/HtmlRenderer';
import ContentPreview from '../ContentPreview/ContentPreview';
import useRowDisplayConditions, { RowDisplayCondition } from 'hooks/queries/useDisplayConditions';
import { useMemo } from 'react';
import { extractDisplayConditionsFromHtml } from 'domains/content/components/Bee/utils/extractDisplayConditions';

export type Props = {
    isOpen: boolean;
    onClose: () => void;
    emailTemplateHTML?: string;
    fromAddress?: string;
    isCloseEnabled?: boolean;
    /** Set to true if this is rendered as an item inside a MUI Grid component. */
    isGridItem?: boolean;
    preHeader?: string;
    /**
     * Highlights selected link by blurring the test of the html.
     */
    selectedLink?: string;
    subjectLine?: string;
    sx?: SxProps<Theme>;
    /**
     * Describes which email is being previewed. i.e. '${whichEmail} email preview'
     */
    whichEmail?: string;
    previewLabel?: string;
};

const EmailPreviewContent = ({
    isOpen,
    onClose,
    emailTemplateHTML,
    fromAddress,
    isCloseEnabled = true,
    isGridItem = false,
    preHeader,
    selectedLink,
    subjectLine,
    sx,
    whichEmail = '',
    previewLabel = `${whichEmail} email preview`,
}: Props) => {
    // Fetch all display conditions
    const { data: rowDisplayConditions } = useRowDisplayConditions();

    // Build map of condition key/id to label from the provided HTML/template
    const displayConditionsMap = useMemo(() => {
        if (!emailTemplateHTML || !rowDisplayConditions) return undefined;
        const conditionKeys = extractDisplayConditionsFromHtml(emailTemplateHTML);
        if (conditionKeys.length > 0) {
            const conditionsMap: Record<string, string> = {};
            conditionKeys.forEach((key) => {
                // If key is of the format conditional_field_{id}, extract the id
                const match = key.match(/^conditional_field_(\d+)$/);
                if (match) {
                    const id = Number(match[1]);
                    const condition = rowDisplayConditions.find((c: RowDisplayCondition) => c.id === id);
                    if (condition) {
                        conditionsMap[key] = condition.label;
                        return;
                    }
                }

                // Fallbacks: attempt to match by label or custom key field if present
                const condition = rowDisplayConditions.find(
                    (c: RowDisplayCondition) => c.label.toLowerCase() === key.toLowerCase() || (c as any).key === key
                );
                conditionsMap[key] = condition ? condition.label : key;
            });
            return Object.keys(conditionsMap).length > 0 ? conditionsMap : undefined;
        }
        return undefined;
    }, [emailTemplateHTML, rowDisplayConditions]);

    return (
        <ContentPreview
            isOpen={isOpen}
            isCloseEnabled={isCloseEnabled}
            isGridItem={isGridItem}
            previewLabel={previewLabel}
            onClose={onClose}
            sx={sx}
        >
            <Stack overflow="auto">
                {(fromAddress || preHeader || subjectLine) && (
                    <Stack direction="row" spacing={3} px={3} py={2} minHeight={112}>
                        <Box alignSelf="center">
                            <img src={CuredLogo} alt="avocado avatar" width="40px" />
                        </Box>
                        <Stack spacing={1}>
                            <Typography variant="body2">{fromAddress ?? ''}</Typography>
                            <Typography variant="overline" color="text.secondary" textTransform="none">
                                {preHeader ?? ''}
                            </Typography>
                            <Typography variant="body2">{subjectLine ?? ''}</Typography>
                        </Stack>
                    </Stack>
                )}

                <Box overflow="auto">
                    <HtmlRenderer
                        htmlContent={emailTemplateHTML ?? ''}
                        selectedLink={selectedLink}
                        displayConditions={displayConditionsMap}
                    />
                </Box>
            </Stack>
        </ContentPreview>
    );
};

export default EmailPreviewContent;
